import React from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
  Divider,
  Checkbox,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const ClientDetailsModal = ({
  openDetailsModal,
  setOpenDetailsModal,
  selectedClient,
  subscriptionPlan,
  setSubscriptionPlan,
  subscriptionNote,
  setSubscriptionNote,
  extendSubscription,
  setExtendSubscription,
  newPin,
  setNewPin,
  isLocked,
  toggleClientLock,
  handleActivateSubscription,
  updateClientPin,
}) => {
  const [openSubscriptionSection, setOpenSubscriptionSection] = React.useState(true);
  const [openPlaylistSection, setOpenPlaylistSection] = React.useState(false);
  const [openClientActionsSection, setOpenClientActionsSection] = React.useState(false);

  return (
    <Modal
      open={openDetailsModal}
      onClose={() => setOpenDetailsModal(false)}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: 600,
          bgcolor: "background.paper",
          p: 4,
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        {selectedClient ? (
          <>
            <Typography variant="h6" gutterBottom>
              Client Details
            </Typography>
            <Typography variant="body1">
              <strong>MAC Address:</strong> {selectedClient.mac}
            </Typography>
            <Typography variant="body1">
              <strong>Subscription End:</strong>{" "}
              {selectedClient.subscriptionEnd
                ? new Date(selectedClient.subscriptionEnd).toLocaleDateString()
                : "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Payment Plan:</strong> {selectedClient.paymentPlan}
            </Typography>
            <Typography variant="body1">
              <strong>Note:</strong> {selectedClient.resellerNote || "N/A"}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Subscription Management Section */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setOpenSubscriptionSection(!openSubscriptionSection)}
              >
                <Typography variant="h6">Subscription Management</Typography>
                <IconButton size="small">
                  {openSubscriptionSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={openSubscriptionSection}>
                <TextField
                  select
                  label="Plan"
                  value={subscriptionPlan}
                  onChange={(e) => setSubscriptionPlan(e.target.value)}
                  fullWidth
                  margin="normal"
                  SelectProps={{ native: true }}
                >
                  <option value="">Select Plan</option>
                  <option value="1 YEAR">1 YEAR</option>
                  <option value="FOREVER">FOREVER</option>
                  <option value="TRIAL">TRIAL</option>
                </TextField>
                <TextField
                  label="Subscription Note"
                  value={subscriptionNote}
                  onChange={(e) => setSubscriptionNote(e.target.value)}
                  fullWidth
                  margin="normal"
                />
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <Checkbox
                    checked={extendSubscription}
                    onChange={(e) => setExtendSubscription(e.target.checked)}
                  />
                  <Typography variant="body1" sx={{ ml: 1 }}>
                    Extend subscription if already active
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  onClick={handleActivateSubscription}
                >
                  {selectedClient.subscriptionEnd ? "Extend Subscription" : "Activate Subscription"}
                </Button>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Playlist Management Section */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setOpenPlaylistSection(!openPlaylistSection)}
              >
                <Typography variant="h6">Playlists</Typography>
                <IconButton size="small">
                  {openPlaylistSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={openPlaylistSection}>
                {selectedClient.source && selectedClient.source.length > 0 ? (
                  selectedClient.source.map((playlist) => (
                    <Box
                      key={playlist.id}
                      sx={{
                        borderBottom: "1px solid #ddd",
                        mb: 1,
                        pb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        <strong>Name:</strong> {playlist.name}
                      </Typography>
                      {playlist.url && (
                        <Typography variant="body2">
                          <strong>URL:</strong> {playlist.url}
                        </Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2">No playlists available.</Typography>
                )}

                <Button variant="contained" sx={{ mt: 2 }}>
                  Add Playlist
                </Button>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Client PIN & Lock Management Section */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setOpenClientActionsSection(!openClientActionsSection)}
              >
                <Typography variant="h6">Client Actions</Typography>
                <IconButton size="small">
                  {openClientActionsSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={openClientActionsSection}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <TextField
                    label="Update PIN"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={updateClientPin}
                  >
                    Update PIN
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={toggleClientLock}
                >
                  {isLocked ? "Unlock Client" : "Lock Client"}
                </Button>
              </Collapse>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => setOpenDetailsModal(false)}
            >
              Close
            </Button>
          </>
        ) : (
          <Typography>Loading details...</Typography>
        )}
      </Box>
    </Modal>
  );
};

export default ClientDetailsModal;
